import axios from '@/apis/axios';
import store from '@/store';

/**
 * 契約情報取得
 */
export const getContract = async () => {
  const urlToken = store.getters['contractor/urlToken'];
  const accessToken = store.getters['contractor/accessToken'];
  if (!urlToken || !accessToken) return new Error('invalid token');
  const response = await axios
    .get(`/contractors/contracts`, {
      headers: { 'access-token': accessToken, 'url-token': urlToken },
    })
    .catch(error => {
      throw error.response.data;
    });

  return response.data;
};

/**
 * 基本情報取得
 */
export const getBaseInfo = async () => {
  const urlToken = store.getters['contractor/urlToken'];
  const accessToken = store.getters['contractor/accessToken'];
  if (!urlToken || !accessToken) return new Error('invalid token');
  const response = await axios
    .get(`/contractors/contracts/base_info`, {
      headers: { 'access-token': accessToken, 'url-token': urlToken },
    })
    .catch(error => {
      throw error.response.data;
    });
  return response.data;
};

/**
 * 代理店情報取得
 */
export const getAgencyInfo = async () => {
  const urlToken = store.getters['contractor/urlToken'];
  const accessToken = store.getters['contractor/accessToken'];
  if (!urlToken || !accessToken) return new Error('invalid token');
  const response = await axios
    .get(`/contractors/contracts/agencies`, {
      headers: { 'access-token': accessToken, 'url-token': urlToken },
    })
    .catch(error => {
      throw error.response.data;
    });
  return response.data;
};

/**
 * 必要書類情報取得
 */
export const getRequiredUploadingDocs = async () => {
  const urlToken = store.getters['contractor/urlToken'];
  const accessToken = store.getters['contractor/accessToken'];
  if (!urlToken || !accessToken) return new Error('invalid token');
  const response = await axios
    .get(`/contractors/contracts/required_uploading_docs`, {
      headers: { 'access-token': accessToken, 'url-token': urlToken },
    })
    .catch(error => {
      throw error.response.data;
    });
  return response.data;
};

/**
 * 契約状況取得
 */
export const getStatus = async () => {
  const urlToken = store.getters['contractor/urlToken'];
  const accessToken = store.getters['contractor/accessToken'];
  if (!urlToken || !accessToken) return new Error('invalid token');
  const response = await axios
    .get(`/contractors/contracts/status`, {
      headers: { 'access-token': accessToken, 'url-token': urlToken },
    })
    .catch(error => {
      throw error.response.data;
    });
  return response.data;
};

/**
 * 契約締結日取得
 */
export const getContractDate = async () => {
  const urlToken = store.getters['contractor/urlToken'];
  const accessToken = store.getters['contractor/accessToken'];
  if (!urlToken || !accessToken) return new Error('invalid token');
  const response = await axios
    .get(`/contractors/contracts/contract_date`, {
      headers: { 'access-token': accessToken, 'url-token': urlToken },
    })
    .catch(error => {
      throw error.response.data;
    });
  return response.data;
};

/**
 * 重要事項等説明書取得
 */
export const getExplanationsForImportantMatter = async () => {
  const urlToken = store.getters['contractor/urlToken'];
  const accessToken = store.getters['contractor/accessToken'];
  if (!urlToken || !accessToken) return new Error('invalid token');
  const response = await axios
    .get(`/contractors/contracts/explanations_for_important_matter`, {
      headers: { 'access-token': accessToken, 'url-token': urlToken },
      responseType: 'blob',
    })
    .catch(error => {
      throw error.response.data;
    });
  return response.data;
};

/**
 * 申請後申込書ダウンロード
 */
export const getCompletedApplicationForm = () =>
  new Promise(async (resolve, reject) => {
    const urlToken = store.getters['contractor/urlToken'];
    const accessToken = store.getters['contractor/accessToken'];
    if (!urlToken || !accessToken) return reject(new Error('invalid token'));

    const response = await axios.get(
      '/contractors/contracts/completed_application_forms/downloads',
      {
        responseType: 'blob',
        dataType: 'application/pdf',
        headers: {
          'access-token': accessToken,
          'url-token': urlToken,
        },
      }
    );
    if (!response) return resolve(null);

    // typeの実態がapplication/octet-streamになっているためpdfに書き換える
    const blob = new Blob([response.data], { type: 'application/pdf' });

    resolve(blob);
  });

/**
 * S3 署名付きPUT URL発行
 */
export const getPresignedUrl = async (fileType, contentType) => {
  const urlToken = store.getters['contractor/urlToken'];
  const accessToken = store.getters['contractor/accessToken'];
  if (!urlToken || !accessToken) throw new Error('invalid token');
  const response = await axios
    .post(
      `/contractors/contracts/presigned-url`,
      { fileType, contentType },
      { headers: { 'access-token': accessToken, 'url-token': urlToken } }
    )
    .catch(error => { throw error; });
  return response.data; // { presignedUrl, s3Key }
};

/**
 * S3 presigned URL に PDF を直接アップロード
 * axios のデフォルトヘッダーが S3 署名検証を壊すため fetch を使用する
 */
export const uploadToS3 = async (presignedUrl, file) => {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': 'application/pdf' },
  });
  if (!response.ok) {
    throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
  }
};

/**
 * 必要書類を一時ストレージにアップロード（5.3b）
 */
export const uploadRequiredFile = async (file, contentType) => {
  const urlToken = store.getters['contractor/urlToken'];
  const accessToken = store.getters['contractor/accessToken'];
  if (!urlToken || !accessToken) throw new Error('invalid token');
  const form = new FormData();
  form.append('file', file);
  const response = await axios
    .post(
      `/contractors/contracts/upload-required-file`,
      form,
      {
        headers: {
          'access-token': accessToken,
          'url-token': urlToken,
          'content-type': 'multipart/form-data',
        },
      }
    )
    .catch(error => { throw error; });
  const resContentType = response.headers['content-type'];
  if (resContentType && resContentType.includes('text/html')) {
    throw new Error('Invalid API response format (HTML received)');
  }
  return response.data; // { s3Key }
};

/**
 * 契約締結処理
 */
export const submitContract = async (selectPlanId, pdfS3Key, requiredFileS3Keys) => {
  const urlToken = store.getters['contractor/urlToken'];
  const accessToken = store.getters['contractor/accessToken'];
  if (!urlToken || !accessToken) throw new Error('invalid token');
  const response = await axios
    .post(
      `/contractors/contracts/submit`,
      { selectPlanId, pdfS3Key, requiredFileS3Keys: requiredFileS3Keys ?? [] },
      { headers: { 'access-token': accessToken, 'url-token': urlToken } }
    )
    .catch(error => {
      throw error;
    });
  const contentType = response.headers['content-type'];
  if (contentType && contentType.includes('text/html')) {
    console.log('Invalid API response format (HTML received)');
    throw new Error('Invalid API response format (HTML received)');
  }
  return response.data ? response.data : response;
};

/**
 * 申込内容以外データ情報取得処理
 */
export const getExtraPictureList = () =>
  new Promise(async (resolve, reject) => {
    const urlToken = store.getters['contractor/urlToken'];
    const accessToken = store.getters['contractor/accessToken'];
    if (!urlToken || !accessToken) return new Error('invalid token');

    const response = await axios.get(
      '/contractors/contracts/extra_files/list',
      {
        headers: {
          'access-token': accessToken,
          'url-token': urlToken,
        },
      }
    );

    resolve(response.data);
  });

/**
 * 申込内容以外データ取得処理
 */
export const getExtraPicture = pictureId =>
  new Promise(async (resolve, reject) => {
    const urlToken = store.getters['contractor/urlToken'];
    const accessToken = store.getters['contractor/accessToken'];
    if (!urlToken || !accessToken) return new Error('invalid token');

    const params = { ['id']: pictureId };

    const response = await axios.get(
      '/contractors/contracts/extra_files/file',
      {
        params: { ...params },
        responseType: 'arraybuffer',
        headers: {
          'access-token': accessToken,
          'url-token': urlToken,
        },
      }
    );
    if (!response) return resolve(null);

    resolve(new Blob([response.data]));
  });

/**
 * 保険契約者勤務先情報取得
 */
export const getContractUserCompanies = async () => {
  const urlToken = store.getters['contractor/urlToken'];
  const accessToken = store.getters['contractor/accessToken'];
  if (!urlToken || !accessToken) return new Error('invalid token');
  const response = await axios
    .get(`/contractors/contract_user/contract_user_company`, {
      headers: { 'access-token': accessToken, 'url-token': urlToken },
    })
    .catch(error => {
      throw error.response.data;
    });

  return response.data;
};
